import { useMemo } from "react";
import { Flex, Space, Typography } from "antd";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer
} from "recharts";
import { formatCurrency } from "../../../helpers/helpers";

const { Text, Title } = Typography;

const CreditLimitChart = ({ businessUser, dealCategoryId }) => {
    const activeBusinessUser = useMemo(
        () =>
            businessUser?.filter((item) => {
                if (!item) return false;
                // Filter by dealCategoryId if provided and present on enterprise item
                if (dealCategoryId && item.dealCategoryId && item.dealCategoryId !== dealCategoryId) {
                    return false;
                }
                const statusId = item.status_id || item.busEntStatusId;
                const statusName = (item.status || item.statusName || "").toString().trim().toUpperCase();
                if (statusId) {
                    if (statusId === "USM1000001" || statusId.toUpperCase() === "PENDING") {
                        return false;
                    }
                    if (statusId === "USM1000002" || statusId.toUpperCase() === "ACTIVE") {
                        return true;
                    }
                }
                if (statusName) {
                    if (statusName === "PENDING") return false;
                    if (statusName === "ACTIVE") return true;
                }
                return true;
            }) ?? [],
        [businessUser, dealCategoryId]
    );

    const totalAmt = useMemo(
        () =>
            activeBusinessUser?.map((item) => ({
                approved: item.approved_credit ? parseInt(item.approved_credit, 10) : 0,
                used: item.used_credit ? parseInt(item.used_credit, 10) : 0,
                balance: item.balance ? parseInt(item.balance, 10) : 0,
            })) ?? [],
        [activeBusinessUser]
    );

    const total = totalAmt.reduce((acc, { approved, used, balance }) => {
        return {
            approved: acc.approved + approved,
            used: acc.used + used,
            balance: acc.balance + balance,
        };
    }, { approved: 0, used: 0, balance: 0 });

    const hasData = total.used > 0 || total.balance > 0;

    const chartData = hasData
        ? [
            { name: "Utilized", value: total.used },
            { name: "Balance", value: total.balance },
        ]
        : [
            { name: "Default", value: 100 }, // full grey circle
        ];

    const dynamicColors = hasData
        ? ["#FF6B00", "#0B1F54"]
        : ["#D9D9D9"]; // grey color

    const utilizedPercentage = total.approved
        ? Math.round((total.used / total.approved) * 100)
        : 0;

    return (
        <div className="credit-limit-chart-container">
            <Flex align="center" justify="space-between">
                <Space direction="vertical" size="large">
                    <div className="credit-limit-details">
                        <Text style={{ fontSize: 14, color: '#40566D' }}>Approved</Text>
                        <Title level={5}>{formatCurrency(total.approved)}</Title>
                    </div>
                    <div className="credit-limit-details">
                        <Text style={{ fontSize: 14, color: '#40566D' }}>Utilized</Text>
                        <Title level={5}>{formatCurrency(Math.abs(total.used))}</Title>
                    </div>
                    <div className="credit-limit-details">
                        <Text style={{ fontSize: 14, color: '#40566D' }}>Balance</Text>
                        <Title level={5}>{formatCurrency(total.balance)}</Title>
                    </div>
                </Space>
                <div className="chart-container">
                    <ResponsiveContainer width="100%" aspect={1}>
                        <PieChart>
                            <Pie
                                data={chartData}
                                cx="50%"
                                cy="50%"
                                innerRadius="60%"
                                outerRadius="80%"
                                paddingAngle={2}
                                dataKey="value"
                            >
                                {chartData.map((_, index) => (
                                    <Cell key={`cell-${index}`} fill={dynamicColors[index]} />
                                ))}
                            </Pie>
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="utilized-percent-container">
                        <Title level={5}>
                            {hasData ? utilizedPercentage : 0}%
                        </Title>
                        <Text style={{ fontSize: 14, color: '#40566D' }}>Utilized</Text>
                    </div>
                </div>
            </Flex>
            <div className="custom-legend">
                <div className="legend-item">
                    <span className="legend-color utilized" />
                    <Text>Utilized</Text>
                </div>
                <div className="legend-item">
                    <span className="legend-color balance" />
                    <Text>Balance</Text>
                </div>
            </div>
        </div>
    );
};

export default CreditLimitChart;
